import React, { useMemo, useRef, useState } from "react";
import {
  Box, TextField, Button, Paper, Typography,
  Table, TableHead, TableRow, TableCell, TableBody
} from "@mui/material";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useReactToPrint } from "react-to-print";
import { db } from "../Config/firebase";
import Navbar from "../Components/Navbar/navbar";
import RupturasDoDiaPrint from "./RupturasDoDiaPrint";
import { consultaTenantAtual } from "../Utils/tenant";

export default function RupturasDoDia() {
  const hoje = new Date().toLocaleDateString("sv-SE");
  const [dataInicial, setDataInicial] = useState(hoje);
  const [dataFinal, setDataFinal] = useState(hoje);
  const [lista, setLista] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [pesquisou, setPesquisou] = useState(false);
  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Controle_Rompimento"
  });

  function formatarDataBR(dataISO) {
    if (!dataISO) return "";
    const [ano, mes, dia] = dataISO.split("-");
    return `${dia}/${mes}/${ano}`;
  }

  function valorPreenchido(valor) {
    return valor !== null && valor !== undefined && String(valor).trim() !== "";
  }

  function dataSemHorario(valor) {
    if (!valor) return null;

    if (typeof valor === "string" && /^\d{4}-\d{2}-\d{2}$/.test(valor)) {
      const [ano, mes, dia] = valor.split("-").map(Number);
      return new Date(ano, mes - 1, dia);
    }

    const data = valor?.toDate ? valor.toDate() : new Date(valor);
    if (Number.isNaN(data.getTime())) return null;
    return new Date(data.getFullYear(), data.getMonth(), data.getDate());
  }

  function formatarDataISO(data) {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const dia = String(data.getDate()).padStart(2, "0");
    return `${ano}-${mes}-${dia}`;
  }

  function rupturaDosagemPreenchida(dosagem, indice, idade) {
    return [1, 2].some((cp) =>
      [
        dosagem[`rupturaCp${cp}TonIdade${indice}`],
        dosagem[`rupturaCp${cp}Idade${indice}`],
        dosagem[`rupturaCp${cp}${idade} dias`]
      ].some(valorPreenchido)
    );
  }

  async function buscarRupturas() {
    if (!dataInicial || !dataFinal || dataInicial > dataFinal) return;

    setBuscando(true);
    setPesquisou(false);
    try {
      const datas = [];
      const dataAtual = new Date(`${dataInicial}T12:00:00`);
      const limite = new Date(`${dataFinal}T12:00:00`);

      while (dataAtual <= limite) {
        datas.push(dataAtual.toLocaleDateString("sv-SE"));
        dataAtual.setDate(dataAtual.getDate() + 1);
      }

      const [snapshots, dosagensSnapshot] = await Promise.all([
        Promise.all(datas.map(data =>
          getDocs(consultaTenantAtual(query(
            collection(db, "tblAgendaRuptura"),
            where("datasRuptura", "array-contains", data)
          )))
        )),
        getDocs(consultaTenantAtual(query(collection(db, "tblDosagensConcreto"))))
      ]);
      const resultado = [];
      const caminhoesPorMoldagem = new Map();

      for (let indice = 0; indice < snapshots.length; indice++) {
        const dataRuptura = datas[indice];

        for (const docMold of snapshots[indice].docs) {
          const moldagem = docMold.data();
          if (moldagem.rupturaExterna === true) continue;

          const indiceData = moldagem.datasRuptura?.indexOf(dataRuptura) ?? -1;
          const idadeCorrespondente = moldagem.idades?.[indiceData];
          if (!idadeCorrespondente) continue;

          if (!caminhoesPorMoldagem.has(docMold.id)) {
            const caminhSnap = await getDocs(
              collection(db, "tblAgendaRuptura", docMold.id, "caminhoes")
            );
            caminhoesPorMoldagem.set(
              docMold.id,
              caminhSnap.docs.map(item => item.data())
            );
          }

          const caminhoesPendentes = caminhoesPorMoldagem.get(docMold.id).filter((caminhao) => {
            if (caminhao.status === "cancelado") return false;
            const resultadoRuptura = caminhao.resultados?.[indiceData];
            return !valorPreenchido(resultadoRuptura?.cp1) &&
              !valorPreenchido(resultadoRuptura?.cp2);
          });
          if (caminhoesPendentes.length === 0) continue;

          const codigos = caminhoesPendentes.map((caminhao) => caminhao.cod_caminhao);
          const modulosElasticidade = caminhoesPendentes.map(
            (caminhao) => caminhao.moduloElasticidade === true
          );
          resultado.push({
            id: docMold.id,
            origem: "agenda",
            cliente: moldagem.cliente,
            obra: moldagem.obra,
            dataMoldagem: moldagem.datamoldagem,
            dataRuptura,
            idade: idadeCorrespondente.dias,
            codigos,
            modulosElasticidade,
            quantidadePares: codigos.length,
            quantidadeCP: codigos.length * 2
          });
        }
      }

      for (const docDosagem of dosagensSnapshot.docs) {
        const dosagem = docDosagem.data();
        if (dosagem.cancelada === true) continue;
        const dataDosagem = dataSemHorario(dosagem.data);
        if (!dataDosagem) continue;

        [0, 1, 2, 3, 4].forEach((indice) => {
          const idadePadrao = ["3", "7", "", "28", ""][indice];
          const idadeValor = dosagem[`idadeRuptura${indice}`] ?? idadePadrao;
          const idade = Number(String(idadeValor).replace(",", "."));
          if (String(idadeValor).trim() === "" || !Number.isFinite(idade) || idade < 0) return;
          if (rupturaDosagemPreenchida(dosagem, indice, idadeValor)) return;

          const dataRupturaCalculada = new Date(dataDosagem);
          dataRupturaCalculada.setDate(dataRupturaCalculada.getDate() + idade);
          const dataRuptura = formatarDataISO(dataRupturaCalculada);
          if (dataRuptura < dataInicial || dataRuptura > dataFinal) return;

          resultado.push({
            id: `dosagem-${docDosagem.id}`,
            origem: "dosagem",
            codigoDosagem: String(dosagem.codigo || "").replace(/\D/g, ""),
            cliente: dosagem.clienteNome,
            obra: `DOSAGENS - ${dosagem.obraNome || "SEM OBRA"}`,
            dataMoldagem: formatarDataISO(dataDosagem),
            dataRuptura,
            idade: idadeValor,
            codigos: [dosagem.codigo || 1],
            quantidadePares: 1,
            quantidadeCP: 2
          });
        });
      }

      setLista(resultado);
      setPesquisou(true);
    } finally {
      setBuscando(false);
    }
  }

  const agrupadoPorObra = useMemo(() => lista.reduce((acc, item) => {
    const obra = item.obra || "SEM OBRA";
    if (!acc[obra]) acc[obra] = [];
    acc[obra].push(item);
    return acc;
  }, {}), [lista]);

  const totalCPPeriodo = lista.reduce(
    (total, item) => total + (item.quantidadeCP || 0),
    0
  );

  return (
    <Box>
      <Navbar />
      <Box p={4}>
        <Typography variant="h5" fontWeight={700} mb={3}>
          Planejamento de Rupturas
        </Typography>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Total de CPs para romper no período: {totalCPPeriodo}
        </Typography>

        <Box mb={3} display="flex" gap={2} flexWrap="wrap">
          <TextField
            type="date"
            label="Data inicial"
            InputLabelProps={{ shrink: true }}
            value={dataInicial}
            onChange={(e) => setDataInicial(e.target.value)}
          />
          <TextField
            type="date"
            label="Data final"
            InputLabelProps={{ shrink: true }}
            value={dataFinal}
            onChange={(e) => setDataFinal(e.target.value)}
          />
          <Button variant="contained" onClick={buscarRupturas} disabled={buscando}>
            {buscando ? "Aguarde..." : "Buscar"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handlePrint}
            disabled={buscando}
          >
            Imprimir
          </Button>
        </Box>

        {pesquisou && Object.keys(agrupadoPorObra).length === 0 && (
          <Typography>Nenhuma ruptura encontrada para este período.</Typography>
        )}

        {Object.keys(agrupadoPorObra).map(nomeObra => (
          <Paper key={nomeObra} sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              {nomeObra}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell>Data Moldagem</TableCell>
                  <TableCell>Data Ruptura</TableCell>
                  <TableCell>Idade (dias)</TableCell>
                  <TableCell>Qtd pares</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agrupadoPorObra[nomeObra].map(item => (
                  <TableRow key={`${item.id}-${item.dataRuptura}`}>
                    <TableCell>{item.cliente}</TableCell>
                    <TableCell>
                      {item.origem === "dosagem"
                        ? `Dosagem${item.codigoDosagem ? ` T${item.codigoDosagem}` : ""}`
                        : "Agenda"}
                    </TableCell>
                    <TableCell>{formatarDataBR(item.dataMoldagem)}</TableCell>
                    <TableCell>{formatarDataBR(item.dataRuptura)}</TableCell>
                    <TableCell>{item.idade}</TableCell>
                    <TableCell>{item.quantidadePares}</TableCell>
                  
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </Box>

      <div style={{ display: "none" }}>
        <div ref={printRef}>
          <RupturasDoDiaPrint
            dados={lista}
            dataInicial={dataInicial}
            dataFinal={dataFinal}
          />
        </div>
      </div>
    </Box>
  );
}
